import * as React from 'react';
import ReactSlick, { Settings } from 'react-slick';
import { ReactComponent as AddedArrowRightIcon } from '../../../assets/svg/icons/arrow-right.svg';
import { ReactComponent as AddedArrowLeftIcon } from '../../../assets/svg/icons/arrow-left.svg';
import { CarouselProps, ArrowProps } from './carousel.types.d';
import { cn } from '../../../helpers';

const Carousel = function Carousel(props: CarouselProps) {
    const { children, carouselSettings, isScrollIntoView, carouselCompKey, arrowsWithPageNumberAtTheBottom = false, isQueryLibraryPage = false, showMoreQueries, records, controlsPanelRef, showTrendingQueries } = props;
    const sliderRef = React.useRef<ReactSlick>(null);
    const leftArrow = React.useRef<SVGSVGElement>(null);
    const rightArrow = React.useRef<SVGSVGElement>(null);
    const [currentSld, setCurrentSld] = React.useState<number>(0);
    const [twoRows, setTwoRows] = React.useState<boolean>(false);
    const [oneRow, setOneRow] = React.useState<boolean>(false);
    const [isRotated, setIsRotated] = React.useState<boolean>(false);
    const [isLandscapeMode, setIsLandscapeMode] = React.useState<boolean>(false);

    const animationDelay = 800; // animation delay
    let totalSlideCount = 0;
    if (carouselSettings) {
        totalSlideCount = carouselSettings.slidesToShow ? carouselSettings.slidesToShow : 0;
    }
    if (carouselSettings && carouselSettings.rows && (showMoreQueries || showTrendingQueries)) {
        if (children && Array.isArray(children) && children.length < 4) {
            carouselSettings.rows = 1;
        } else {
            carouselSettings.rows = 2;
        }
    }

    const showQueriesSection = () => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        const isMobileDevice = window.matchMedia('(pointer:coarse)').matches;
        if (width > height && isMobileDevice && carouselSettings && carouselSettings.rows === 2) {
            setIsLandscapeMode(true);
            const slickTrack = document.getElementsByClassName('slick-slide');
            const slickList = document.getElementsByClassName('slick-list');
            if ((showMoreQueries || showTrendingQueries) && slickTrack) {
                if (slickList && slickList[0] && slickList[0].clientHeight === 0) {
                    (slickList[0] as HTMLElement).style.height = 'auto';
                    setIsRotated(!isRotated);
                }
            }
        }
        if (width < height && isMobileDevice && isLandscapeMode) {
            setIsRotated(!isRotated);
            setIsLandscapeMode(false);
            if (sliderRef.current) sliderRef.current.slickGoTo(currentSld);
        }
    };

    React.useEffect(() => {
        if (showMoreQueries || showTrendingQueries) showQueriesSection();
    });

    const calculateTilesTabIndex = () => {
        setTimeout(() => {
            const tracks = document.getElementsByClassName('slick-slide');

            const applyTabIndex = (element: any, tabIndex: string) => {
                if (element.children.length > 0) {
                    Array.from(element.children).forEach(child => {
                        applyTabIndex(child, tabIndex);
                    });
                }

                // we expect user will iteract only with links inside carousel
                if (element.tagName === 'A' && !element.className.includes('dummy-block')) element.setAttribute('tabindex', tabIndex);
            };
            Array.from(tracks).forEach(el => {
                // imaged tile subelement is <a>
                const isImagedTile = el.children.length && el.children[0].children.length && el.children[0].children[0].tagName === 'A';
                if (el.classList.contains('slick-active')) {
                    // this condition is only for image-tiles carousel. We are 100% sure about children structure
                    // if second child is not <a>, we need to allof user focus slide-element by tab, to activate arrows
                    if (!isImagedTile) el.setAttribute('tabindex', isQueryLibraryPage ? '-1' : '0');
                    applyTabIndex(el, '0');
                } else {
                    if (!isImagedTile) el.setAttribute('tabindex', '-1');
                    applyTabIndex(el, '-1');
                }
            });
        }, animationDelay);
    };

    // scroll to the first slide if childrens changes
    // can be applyed only for QueryLibraryPage as its children changes only on search action
    React.useEffect(() => {
        if (isQueryLibraryPage) {
            if (currentSld !== 0) setCurrentSld(0);
            if (sliderRef.current) sliderRef.current.slickGoTo(0);
        }
        calculateTilesTabIndex();
    }, [children]);

    React.useEffect(() => {
        setTimeout(() => {
            const dotButtons = document.querySelectorAll('.slick-dots button');
            const dotButtons1 = document.querySelectorAll('.slick-dots li');
            dotButtons.forEach(button => {
                button.setAttribute('tabIndex', '-1');
            });
            dotButtons1.forEach(button => {
                button.setAttribute('tabIndex', '-1');
            });
        }, 500);
    }, []);

    // slides switching for bottom progress-navigation bar. As example it is on QuryLibraryCatalog page
    const progressBottomArrowsClick = (dir: 'prev' | 'next') => {
        const sld = sliderRef.current;
        if (sld) {
            if (dir === 'prev') {
                sld.slickPrev();
            } else {
                sld.slickNext();
            }
        }
        // scroll page to the top of the slider + 100px
        if (controlsPanelRef && controlsPanelRef.current) {
            controlsPanelRef.current.scrollIntoView({ block: 'start', behavior: 'instant' });
        }
    };

    // the function is used only for TABbing
    function arrowPress(e: React.KeyboardEvent, rightArr: boolean) {
        if (e.key === 'Enter' && sliderRef.current) {
            if (rightArr) {
                if (arrowsWithPageNumberAtTheBottom) return progressBottomArrowsClick('next');

                sliderRef.current.slickNext();
                setTimeout(() => {
                    if (leftArrow.current) leftArrow.current.focus();
                }, animationDelay);
            } else {
                if (arrowsWithPageNumberAtTheBottom) return progressBottomArrowsClick('prev');

                sliderRef.current.slickPrev();
                setTimeout(() => {
                    if (rightArrow.current) rightArrow.current.focus();
                }, animationDelay);
            }
        }

        return null;
    }

    function NextArrow(propsarrow: ArrowProps) {
        const { currentSlide, slideCount, onClick } = propsarrow;
        if (slideCount) {
            if (currentSlide && (currentSlide + totalSlideCount) >= slideCount) return (<AddedArrowRightIcon style={{ opacity: 0.4, fill: '#000' }} className="slick-next__slick-disabled slick-arrow slick-next" />);
        }
        return (<AddedArrowRightIcon tabIndex={0} className="slick-arrow slick-next" id="slick-next" ref={rightArrow} onClick={onClick} onKeyDown={e => arrowPress(e, true)} />);
    }

    function PrevArrow(propsarrow: ArrowProps) {
        const { currentSlide, onClick } = propsarrow;
        if (currentSlide === 0) return (<AddedArrowLeftIcon style={{ opacity: 0.4, fill: '#000' }} className="slick-prev__slick-disabled slick-arrow slick-prev" />);

        return (<AddedArrowLeftIcon tabIndex={0} className="slick-arrow slick-prev" id="slick-prev" ref={leftArrow} onClick={onClick} onKeyDown={e => arrowPress(e, false)} />);
    }

    const handleBeforeChange = (cur: number, next: number) => {
        setCurrentSld(next);
        if (carouselSettings && carouselSettings.rows && (showMoreQueries || showTrendingQueries)) {
            const numOfPages = records && Array.isArray(records) ? Math.floor(records.length / 6) : 1;
            if (records && Array.isArray(records) && next === numOfPages * 3 && records.length % 6 <= 3) {
                carouselSettings.rows = 1;
                setOneRow(true);
                setTwoRows(false);
            } else if (!twoRows) {
                carouselSettings.rows = 2;
                setTwoRows(true);
                setOneRow(false);
            }
        }
    };


    const [arrowEnabled, setArrow] = React.useState(false);
    const slickOptionsDefault = {
        arrows: !arrowsWithPageNumberAtTheBottom ? arrowEnabled : false,
        dots: true,
        draggable: true,
        focusOnSelect: !carouselCompKey,
        infinite: false,
        speed: !arrowsWithPageNumberAtTheBottom ? 500 : 0,
        nextArrow: !arrowsWithPageNumberAtTheBottom && <NextArrow />,
        prevArrow: !arrowsWithPageNumberAtTheBottom && <PrevArrow />,
        responsive: [],
        beforeChange: (cur: number, next: number) => {
            handleBeforeChange(cur, next);
        },
        afterChange: () => {
            calculateTilesTabIndex();
        }
    };

    function handleMouseOver() {
        if (window.innerWidth > 1023) {
            setArrow(true);
        }
    }
    function handleMouseOut() {
        setArrow(false);
    }

    const options = { ...slickOptionsDefault, ...carouselSettings } as Settings;

    return children ? (
        <div
            role="presentation"
            onMouseEnter={handleMouseOver}
            onMouseLeave={handleMouseOut}
            onFocus={handleMouseOver}
            key={carouselCompKey || undefined}
            className={cn('carousel-wrap', {
                'carousel-wrap__more-queries': showMoreQueries || showTrendingQueries,
                'carousel-wrap__relatedQueries-two-rows': (carouselSettings && carouselSettings.rows === 2) || twoRows,
                'carousel-wrap__relatedQueries-one-row': oneRow
            })}
        >
            <ReactSlick ref={sliderRef} {...options} className="carousel">
                {children}
            </ReactSlick>
            {/* Progress component */}
            {arrowsWithPageNumberAtTheBottom && Array.isArray(children) && children.length > 1 && (
                <div className="arrows-bottom">
                    <PrevArrow
                        slideCount={children.length}
                        currentSlide={currentSld}
                        onClick={() => progressBottomArrowsClick('prev')}
                    />
                    <div className="progress">
                        {currentSld + 1} of {children.length} Pages
                    </div>
                    <NextArrow
                        slideCount={children.length}
                        currentSlide={currentSld}
                        onClick={() => progressBottomArrowsClick('next')}
                    />
                </div>
            )}
        </div>
    ) : null;
};

export default React.memo(Carousel);
